import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ArrowRight, Building2, Eye, EyeOff, Loader2, UserRound } from 'lucide-react';
import { LOGIN_ACCOUNT_TYPES } from '@/constants/login';
import type { LoginAccountType } from '@/types/accountType';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Giriş / kayıt formlarının ortak parçaları; görünüm Giriş / Kayıt tasarımlarındandır.

/** Candidate / employer switch at the top of the sign-in and sign-up forms. */
export const AccountTypeTabs = ({
  value,
  onChange,
}: {
  value: LoginAccountType;
  onChange: (type: LoginAccountType) => void;
}) => {
  const { t } = useTranslation();
  const tabs = [
    { type: LOGIN_ACCOUNT_TYPES.candidate, label: t('auth.login.tabCandidate'), icon: UserRound },
    { type: LOGIN_ACCOUNT_TYPES.employer, label: t('auth.login.tabEmployer'), icon: Building2 },
  ];
  return (
    <Tabs value={value} onValueChange={(type) => onChange(type as LoginAccountType)} className='mt-[30px]'>
      <TabsList
        aria-label={t('auth.login.tabsLabel')}
        className='grid h-auto w-full grid-cols-2 gap-1.5 rounded-[14px] border-0 bg-public-warm-tab p-1.5'
      >
        {tabs.map(({ type, label, icon: Icon }) => (
          <TabsTrigger
            key={type}
            value={type}
            className='mb-0 h-[46px] gap-2.5 rounded-[10px] border-0 text-[14.5px] font-medium text-public-dim hover:text-public-ink data-[state=active]:bg-white data-[state=active]:font-semibold data-[state=active]:text-public-ink data-[state=active]:shadow-[0_6px_14px_-8px_rgba(14,20,36,.3)] data-[state=active]:[&>svg]:text-public-accent'
          >
            <Icon className='h-[18px] w-[18px]' strokeWidth={1.8} aria-hidden='true' />
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
};

/** Form title; sign-up forms show an icon badge on the right. */
export const AuthFormHead = ({ title, subtitle, icon }: { title: string; subtitle?: string; icon?: ReactNode }) => (
  <div className='relative pr-16'>
    {icon && (
      <span className='absolute -top-1 right-0 flex h-[52px] w-[52px] items-center justify-center rounded-[14px] bg-public-accent-soft text-public-accent'>
        {icon}
      </span>
    )}
    <h1 className='mt-3.5 text-[32px] font-semibold leading-[1.05] tracking-[-.04em] text-public-ink sm:text-[38px]'>
      {title}
    </h1>
    {subtitle && <p className='mt-2.5 text-[15.5px] leading-normal text-public-muted'>{subtitle}</p>}
  </div>
);

interface AuthFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  autoComplete?: string;
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
  inputMode?: 'numeric' | 'tel';
  onBlur?: () => void;
  'aria-invalid'?: boolean;
  /** Large centered digits, e.g. the e-mail code. */
  code?: boolean;
  /** Small tag after the label, e.g. "optional". */
  tag?: string;
  /** Help text under the input. */
  hint?: string;
}

/** A labelled input; password inputs get a show/hide toggle. */
export const AuthField = ({
  label,
  value,
  onChange,
  type = 'text',
  tag,
  hint,
  code = false,
  ...input
}: AuthFieldProps) => {
  const { t } = useTranslation();
  const id = useId();
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === 'password';
  return (
    <div className='mb-[18px]'>
      <Label htmlFor={id} className='mb-2 flex items-center gap-2 text-[13.5px] font-semibold text-public-ink'>
        {label}
        {tag && <span className='text-xs font-normal text-public-dim'>{tag}</span>}
      </Label>
      <div className='relative'>
        <Input
          id={id}
          type={isPassword && revealed ? 'text' : type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-password={isPassword}
          data-code={code}
          className='h-[50px] rounded-[12px] border-[var(--public-line-mid)] bg-white px-4 text-[15px] data-[code=true]:text-center data-[code=true]:text-lg data-[code=true]:tracking-[.5em] data-[password=true]:pr-12'
          {...input}
        />
        {isPassword && (
          <Button
            type='button'
            variant='ghost'
            size='icon'
            onClick={() => setRevealed((v) => !v)}
            aria-label={t(revealed ? 'auth.login.hidePassword' : 'auth.login.showPassword')}
            className='absolute right-1 top-1/2 h-10 w-10 -translate-y-1/2 rounded-[10px] text-public-dim hover:text-public-ink'
          >
            {revealed ? (
              <Eye className='h-[19px] w-[19px]' aria-hidden='true' />
            ) : (
              <EyeOff className='h-[19px] w-[19px]' aria-hidden='true' />
            )}
          </Button>
        )}
      </div>
      {hint && <small className='mt-2 block text-xs text-public-dim'>{hint}</small>}
    </div>
  );
};

/** A consent-style checkbox; `children` may hold links. */
export const AuthCheckbox = ({
  checked,
  onChange,
  ariaLabel,
  children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel?: string;
  children: ReactNode;
}) => {
  const id = useId();
  return (
    <div className='flex items-start gap-3'>
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(state) => onChange(state === true)}
        aria-label={ariaLabel}
        className='mt-px h-5 w-5 rounded-[6px] border-[1.5px] border-[var(--public-line-accent)] bg-white data-[state=checked]:border-public-accent data-[state=checked]:bg-public-accent [&_svg]:h-3.5 [&_svg]:w-3.5'
      />
      <Label htmlFor={id} className='cursor-pointer text-[13px] font-normal leading-[1.45] text-public-muted'>
        {children}
      </Label>
    </div>
  );
};

export const AuthConsentBox = ({ children }: { children: ReactNode }) => (
  <div className='mt-1 grid gap-3 rounded-[14px] bg-public-warm-mid px-5 py-[18px]'>{children}</div>
);

/** Link inside consent / legal text. */
export const AuthInlineLink = ({ href, children }: { href: string; children: ReactNode }) => (
  <a
    href={href}
    target='_blank'
    rel='noopener noreferrer'
    className='text-public-accent underline decoration-[rgba(43,68,245,.35)] underline-offset-[3px]'
  >
    {children}
  </a>
);

export const AuthError = ({ message }: { message: string | null }) =>
  message ? (
    <p role='alert' className='mt-5 rounded-[12px] bg-destructive/10 px-4 py-3 text-sm text-destructive'>
      {message}
    </p>
  ) : null;

/** The primary submit button; `note` is the handwritten hint above it (Giriş / Kayıt tasarımları). */
export const AuthSubmitButton = ({
  label,
  loading = false,
  loadingLabel,
  disabled = false,
  note,
}: {
  label: string;
  loading?: boolean;
  loadingLabel?: string;
  disabled?: boolean;
  note?: string;
}) => (
  <div className='relative mt-10'>
    {note && (
      <div
        aria-hidden='true'
        className='absolute -top-[30px] right-1.5 rotate-[-3deg] whitespace-nowrap font-[Caveat,cursive] text-[21px] leading-none text-public-accent'
      >
        {note}
      </div>
    )}
    <Button
      type='submit'
      disabled={disabled || loading}
      className='h-[54px] w-full justify-between rounded-[12px] bg-public-ink px-[22px] text-[15.5px] font-semibold text-white shadow-[0_18px_30px_-18px_rgba(14,20,36,.6)] hover:bg-public-ink-hover active:bg-public-ink disabled:bg-public-ink disabled:opacity-60'
    >
      {loading && loadingLabel ? loadingLabel : label}
      {loading ? (
        <Loader2 className='h-[19px] w-[19px] animate-spin' aria-hidden='true' />
      ) : (
        <ArrowRight className='h-[19px] w-[19px]' aria-hidden='true' />
      )}
    </Button>
  </div>
);

/** A secondary full-width action with an icon, e.g. "create a candidate account". */
export const AuthSecondaryLink = ({ to, icon, children }: { to: string; icon: ReactNode; children: ReactNode }) => (
  <Button
    asChild
    variant='outline'
    className='h-[50px] gap-2.5 rounded-[12px] border-[var(--public-line-mid)] bg-white px-3 text-sm font-semibold text-public-ink hover:bg-public-warm hover:text-public-ink'
  >
    <Link to={to}>
      {icon}
      {children}
    </Link>
  </Button>
);
